import React, { useEffect, useRef, useState } from 'react';
import { siteData } from '../data/mock';
import { Calculator, Container, FileSearch, Target, Zap } from 'lucide-react';

const iconMap = {
  Calculator,
  Container,
  FileSearch,
  Target,
};

export const ProductEngineSection = () => {
  const { productEngine } = siteData;
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.06 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="product-engine"
      className="oa-section"
      style={{ background: 'var(--bg-secondary)', borderTop: '1px solid var(--border-subtle)' }}
    >
      <div className="relative z-10" style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Section Label */}
        <div
          className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          style={{ marginBottom: '24px' }}
        >
          <span className="oa-number">{productEngine.number} // {productEngine.label}</span>
        </div>

        {/* Headline */}
        <h2
          className={`oa-display-large transition-all duration-700 delay-100 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          style={{ marginBottom: '24px', maxWidth: '800px' }}
        >
          {productEngine.headline}
        </h2>

        {/* Body */}
        <p
          className={`oa-body-medium transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          style={{ marginBottom: '80px', maxWidth: '720px' }}
        >
          {productEngine.body}
        </p>

        {/* Comparison Block */}
        <div
          className={`grid grid-cols-1 md:grid-cols-2 gap-0 transition-all duration-700 delay-300 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          style={{ marginBottom: '60px' }}
        >
          {/* Left — Generative AI (dimmed) */}
          <div
            style={{
              padding: '48px 40px',
              background: 'rgba(255,255,255,0.02)',
              borderRight: '1px solid var(--border-subtle)',
              opacity: 0.5,
            }}
          >
            <span
              style={{
                fontSize: '12px',
                fontWeight: 500,
                letterSpacing: '2px',
                textTransform: 'uppercase',
                color: 'var(--text-muted)',
                fontFamily: "'Inter', sans-serif",
                display: 'block',
                marginBottom: '8px',
              }}
            >
              {productEngine.comparison.left.title}
            </span>
            <span className="oa-body-small" style={{ color: 'var(--text-muted)', display: 'block', marginBottom: '28px' }}>
              {productEngine.comparison.left.subtitle}
            </span>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {productEngine.comparison.left.points.map((pt, i) => (
                <li key={i} className="flex items-center gap-3">
                  <div style={{ width: '6px', height: '6px', background: 'var(--text-muted)', flexShrink: 0 }} />
                  <span className="oa-body-small" style={{ color: 'var(--text-muted)' }}>{pt}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Right — Agentic AI (highlighted) */}
          <div
            style={{
              padding: '48px 40px',
              background: 'rgba(0, 255, 209, 0.03)',
              borderLeft: '2px solid var(--brand-primary)',
            }}
          >
            <span
              style={{
                fontSize: '12px',
                fontWeight: 500,
                letterSpacing: '2px',
                textTransform: 'uppercase',
                color: 'var(--brand-primary)',
                fontFamily: "'Inter', sans-serif",
                display: 'block',
                marginBottom: '8px',
              }}
            >
              {productEngine.comparison.right.title}
            </span>
            <span className="oa-body-small" style={{ color: 'var(--text-primary)', display: 'block', marginBottom: '28px' }}>
              {productEngine.comparison.right.subtitle}
            </span>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {productEngine.comparison.right.points.map((pt, i) => (
                <li key={i} className="flex items-center gap-3">
                  <div style={{ width: '6px', height: '6px', background: 'var(--brand-primary)', flexShrink: 0 }} />
                  <span className="oa-body-small" style={{ color: 'var(--text-primary)' }}>{pt}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Callout: The Compounding Advantage */}
        <div
          className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          style={{
            transitionDelay: '400ms',
            padding: '48px 40px',
            borderLeft: '2px solid var(--brand-primary)',
            background: 'rgba(0, 255, 209, 0.02)',
            marginBottom: '80px',
          }}
        >
          <div className="flex items-center gap-3" style={{ marginBottom: '16px' }}>
            <Zap size={18} style={{ color: 'var(--brand-primary)' }} />
            <h3 className="oa-heading-2" style={{ color: 'var(--brand-primary)' }}>
              {productEngine.callout.title}
            </h3>
          </div>
          <p className="oa-body-medium" style={{ maxWidth: '800px' }}>
            {productEngine.callout.body}
          </p>
        </div>

        {/* Deployed Capabilities */}
        <div
          className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          style={{ transitionDelay: '500ms', marginBottom: '16px' }}
        >
          <span className="oa-number" style={{ marginBottom: '32px', display: 'block' }}>Deployed Capabilities</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
          {productEngine.capabilities.map((cap, i) => {
            const Icon = iconMap[cap.icon];
            return (
              <div
                key={i}
                className={`group transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                style={{
                  transitionDelay: `${600 + i * 80}ms`,
                  padding: '40px',
                  borderRight: i % 2 === 0 ? '1px solid var(--border-subtle)' : 'none',
                  borderBottom: i < 2 ? '1px solid var(--border-subtle)' : 'none',
                }}
              >
                <div className="flex items-center gap-4" style={{ marginBottom: '20px' }}>
                  <div
                    style={{
                      width: '44px',
                      height: '44px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: 'rgba(0, 255, 209, 0.06)',
                    }}
                  >
                    {Icon && <Icon size={20} style={{ color: 'var(--brand-primary)' }} />}
                  </div>
                  <div>
                    <h4 className="oa-heading-3" style={{ color: 'var(--text-primary)', marginBottom: '2px' }}>
                      {cap.title}
                    </h4>
                    <span
                      style={{
                        fontSize: '12px',
                        color: 'var(--brand-primary)',
                        fontFamily: "'Inter', sans-serif",
                        letterSpacing: '1px',
                        textTransform: 'uppercase',
                      }}
                    >
                      {cap.sector}
                    </span>
                  </div>
                </div>
                <p className="oa-body-small">{cap.description}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
